import { memo, useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react'
import { useFilters, useModal, useSession, useTranslator } from '@/hooks'
import styled from 'styled-components'
import { motion, type Variants } from 'motion/react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { MODALS, TypeFilter } from '@/context'
import { getTransition, mq, presets, presetsFluid } from '@/styles'
import { Button, Img } from '../../base'
import { usePathname, useSearchParams } from 'next/navigation'
import { arraysContainsSameValues, DY_GUEST_CART_ALLOWED } from '@/utils'
import { E_FILTER_TYPE } from '@/schema'

type TypeFormInputs = {
  sort: 'recommended' | 'highest_price' | 'lowest_price'
  brand: string[]
  [key: string]: boolean | string | string[] // Dynamic boolean filters
}

const defaultValues:TypeFormInputs = {
  sort: 'recommended',
  brand: []
}

export const FormFilters:FC = memo(() => {
  const { currentFilters, setActiveFilters, activeFilters, updateUrlWithFilters } = useFilters()
  const pathname = usePathname()

  const { register, handleSubmit, formState: { errors: formErrors }, reset, watch } = useForm<TypeFormInputs>({
    defaultValues: activeFilters?.length ? {
      sort: (() => {
        const sortFilter = activeFilters.find(item => item.param === 'sort')
        if (!sortFilter) return 'recommended'
        const value = sortFilter.value
        return (!Array.isArray(value) && value?.slug) || 'recommended'
      })() as TypeFormInputs['sort'],
      brand: (() => {
        const brandFilter = activeFilters.find(item => item.param === 'brand')
        if (!brandFilter) return []
        const value = brandFilter.value
        return Array.isArray(value) ? value.map(v => v.slug) : []
      })(),
      // Initialize dynamic filters (BOOLEAN and SELECT types)
      ...Object.keys(currentFilters || {})
        .filter(key => key !== 'brands')
        .reduce((acc, key) => {
          const filterData = currentFilters[key]
          const filters = Array.isArray(filterData) ? filterData : [filterData]
          const filterType = filters[0]?.type

          // Handle BOOLEAN filters - store as boolean
          if (filterType === E_FILTER_TYPE.BOOLEAN) {
            const booleanFilter = activeFilters.find(item => item.param === filters[0]?.slug)
            acc[key] = !!booleanFilter
          }

          // Handle SELECT filters - store as string[] (array with selected slugs)
          if (filterType === E_FILTER_TYPE.SELECT) {
            const selectFilter = activeFilters.find(item => item.param === filters[0]?.parentSlug)
            const selectedSlugs = Array.isArray(selectFilter?.value)
              ? selectFilter.value.map(v => v.slug)
              : []
            acc[key] = selectedSlugs
          }

          return acc
        }, {} as Record<string, boolean | string[]>)
    } : defaultValues,
    mode: 'onChange'
  })

  const { t } = useTranslator()
  const { closeClearingPayload, payload, isActive } = useModal(MODALS.FILTERS)
  const { user } = useSession()
  const [sortBlockExpanded, setSortBlockExpanded] = useState(false)
  const [brandsBlockExpanded, setBrandsBlockExpanded] = useState(false)
  const [healthBlockExpanded, setHealthBlockExpanded] = useState(false)
  // Add state for each SELECT filter's expand/collapse
  const [selectFiltersExpanded, setSelectFiltersExpanded] = useState<Record<string, boolean>>({})
  const searchParams = useSearchParams()
  const prevPathnameRef = useRef<string | null>(null)
  const searchParamSort = searchParams.get('sort')
  const searchParamBrand = searchParams.get('brand')

  // Watch form values and submit on change
  const watchedValues = watch()

  // Get boolean filters from currentFilters
  const booleanFilters = useMemo(() => {
    if (!currentFilters) return []
    return Object.entries(currentFilters)
      .filter(([key]) => key !== 'brands')
      .map(([param, filter]) => ({
        param,
        filter: Array.isArray(filter) ? filter[0] : filter
      }))
      .filter(({ filter }) => filter?.type === E_FILTER_TYPE.BOOLEAN)
  }, [currentFilters])

  // Get SELECT filters from currentFilters (same pattern as brands)
  const selectFilters = useMemo(() => {
    if (!currentFilters) return {}

    return Object.entries(currentFilters)
      .filter(([key]) => key !== 'brands')
      .reduce((acc, [param, filters]) => {
        const filterArray = Array.isArray(filters) ? filters : [filters]
        // Check if first item is SELECT type
        if (filterArray[0]?.type === E_FILTER_TYPE.SELECT) {
          acc[param] = filterArray
        }
        return acc
      }, {} as Record<string, TypeFilter[]>)
  }, [currentFilters])

  // Create a stable reference to boolean filter values
  const booleanFilterValuesString = useMemo(() => {
    const values = booleanFilters.reduce((acc, { param }) => {
      acc[param] = watchedValues[param]
      return acc
    }, {} as Record<string, unknown>)
    return JSON.stringify(values)
  }, [booleanFilters, watchedValues])

  // Create stable string representation of SELECT filter values
  const selectFilterValuesString = useMemo(() => {
    const values = Object.keys(selectFilters).reduce((acc, param) => {
      acc[param] = watchedValues[param]
      return acc
    }, {} as Record<string, unknown>)
    return JSON.stringify(values)
  }, [selectFilters, watchedValues])

  const sortOptions = useMemo(() => (user || DY_GUEST_CART_ALLOWED) ? [
    { value: 'recommended', name: t('project.modals.filters.recommended') as string },
    { value: 'highest_price', name: t('project.modals.filters.highest_price') as string },
    { value: 'lowest_price', name: t('project.modals.filters.lowest_price') as string }
  ] : [{ value: 'recommended', name: t('project.modals.filters.recommended') as string }], [user, t])

  const SortInputs = useMemo(() => sortOptions.map(({ value, name }) => {
    const inputId = `sort-${value}`
    const checked = watchedValues.sort === value

    return (
      <Label key={value} htmlFor={inputId} $checked={checked}>
        <input type='radio' id={inputId} value={value} {...register('sort')} defaultChecked={checked} />
        {name}
        <div>
          <HexagonSvg checked={checked} />
          <DivCircle $checked={checked} />
        </div>
      </Label>
    )
  }), [sortOptions, register, watchedValues.sort])

  const BrandInputs = useMemo(() => currentFilters.brands.map((brand, idx) => {
    const inputId = `brand-${brand.id}`
    const checked = Array.isArray(watchedValues.brand) && watchedValues.brand.includes(brand.slug)

    return (
      <Label key={`${idx}-${brand.id}`} htmlFor={inputId} $checked={checked} variants={itemVariants(checked)}>
        <input type='checkbox' id={inputId} value={brand.slug} {...register('brand')} defaultChecked={checked} />
        {brand.name}
        <div>
          <HexagonSvg checked={checked} />
          <Img media={mediaTick} lazy={true} />
        </div>
      </Label>
    )
  }), [currentFilters, register, watchedValues.brand])

  // Generate boolean filter fieldsets (simple, no collapse)
  const BooleanInputs = useMemo(() => {
    return booleanFilters.map(({ param, filter }) => {
      const inputId = `filter-${param}`
      const checked = !!watchedValues[param]

      return (
        // <fieldset key={param}>
        //   <legend>{filter.name}</legend>
        <Label key={param} htmlFor={inputId} $checked={checked}>
          <input
            type='checkbox'
            id={inputId}
            {...register(param)}
            defaultChecked={checked}
          />
          {filter.name}
          <div>
            <HexagonSvg checked={checked} />
            <Img media={mediaTick} lazy={true} />
          </div>
        </Label>
        // </fieldset>
      )
    })
  }, [booleanFilters, register, watchedValues])

  // Generate one fieldset per SELECT filter
  const SelectFilterFieldsets = useMemo(() => {
    return Object.entries(selectFilters).map(([param, filters]) => {
      if (!filters.length) return null

      const parentName = filters[0].parentName // e.g., "Regions", "Technique"
      // const parentSlug = filters[0].parentSlug
      const isExpanded = selectFiltersExpanded[param] ?? false
      const selectedValues = watchedValues[param] as string[] || []

      // Generate radio inputs for this SELECT filter
      const SelectInputs = filters.map((filter) => {
        const inputId = `select-${param}-${filter.id}`
        const checked = selectedValues.includes(filter.slug)

        return (
          <Label key={filter.id} htmlFor={inputId} $checked={checked}>
            <input
              type='checkbox'
              id={inputId}
              value={filter.slug}
              {...register(param)}
              defaultChecked={checked}
            />
            {filter.name}
            <div>
              <HexagonSvg checked={checked} />
              <Img media={mediaTick} lazy={true} />
            </div>
          </Label>
        )
      })

      return (
        <fieldset key={param}>
          <legend>
            <button
              type='button'
              onClick={() => setSelectFiltersExpanded(prev => ({ ...prev, [param]: !prev[param] }))}
              aria-controls={`${param}-filters`}
            >
              {parentName}
              <div className={`img-wrapper${isExpanded ? ' expanded' : ''}`}>
                <Img media={mediaChevron} lazy={true} />
              </div>
            </button>
            <motion.p
              className='resume'
              initial={{ opacity: 0 }}
              animate={isExpanded ? { opacity: 0 } : { opacity: 1 }}
              transition={{ delay: .1 }}
            >
              {filters.find(f => selectedValues.includes(f.slug))?.name || ''}
            </motion.p>
          </legend>
          <motion.div
            id={`${param}-filters`}
            initial={'inactive'}
            animate={isExpanded ? 'active' : 'inactive'}
            exit={'exit'}
            variants={blockVariants}
            aria-expanded={isExpanded}
          >
            {SelectInputs}
          </motion.div>
        </fieldset>
      )
    })
  }, [selectFilters, selectFiltersExpanded, register, watchedValues])


  const onClickReset = useCallback(() => {
    const resetValues: TypeFormInputs = {
      sort: 'recommended',
      brand: []
    }

    // Add boolean filters to reset values
    booleanFilters.forEach(({ param }) => {
      resetValues[param] = false
    })

    // Add select filters to reset values
    Object.keys(selectFilters).forEach((param) => {
      resetValues[param] = []
    })

    reset(resetValues)
  }, [reset, booleanFilters, selectFilters])

  const onSubmit: SubmitHandler<TypeFormInputs> = useCallback(async (formData) => {
    const { sort, brand, ...otherFilters } = formData

    const incomingActiveFilters = []

    if(sort) incomingActiveFilters.push({
      param: 'sort',
      value: {
        name: sortOptions.find(option => option.value === sort)?.name || '',
        slug: sort
      }
    })

    if(brand && brand?.length > 0) incomingActiveFilters.push({
      param: 'brand',
      value: brand.map(val => ({
        name: currentFilters.brands.find(item => item.slug === val)?.name || '',
        slug: val,
      }))
    })

    // Process other filters (BOOLEAN and SELECT)
    Object.entries(otherFilters).forEach(([param, value]) => {
      // Check if it's a SELECT filter
      if (selectFilters[param]) {
        const selectedSlugs = Array.isArray(value) ? value : (value ? [value] : [])
        if (selectedSlugs.length > 0) {
          const parentSlug = selectFilters[param][0].parentSlug
          incomingActiveFilters.push({
            param: parentSlug, // Use parent slug as param
            value: selectedSlugs.map(slug => {
              const filter = selectFilters[param].find(f => f.slug === slug)
              return {
                name: filter?.name || '',
                slug
              }
            })
          })
        }
      }
      // Check if it's a BOOLEAN filter
      else if (value === true) {
        const booleanFilter = booleanFilters.find(f => f.param === param)
        if (booleanFilter) {
          incomingActiveFilters.push({
            param: booleanFilter.filter.slug,
            value: {
              name: booleanFilter.filter.name,
              slug: true
            }
          })
        }
      }
    })


    setActiveFilters(incomingActiveFilters)
    updateUrlWithFilters(incomingActiveFilters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, sortOptions, selectFilters, booleanFilters])

  const onClickToggleFieldset = useCallback((type) => {
    if (type === 'sort') setSortBlockExpanded(prev => !prev)
    else if (type === 'brands') setBrandsBlockExpanded(prev => !prev)
    else if (type === 'health') setHealthBlockExpanded(prev => !prev)
  }, [])

  const closeModal = () => {
    closeClearingPayload()
    setSortBlockExpanded(false)
    setBrandsBlockExpanded(false)
    setHealthBlockExpanded(false)
  }

  // Reset form when navigating to a different catalog category (not when opening/closing product drawer)
  useEffect(() => {
    const isProductPage = pathname?.includes('/product/')
    if (isProductPage) {
      prevPathnameRef.current = pathname
      return
    }
    const prev = prevPathnameRef.current
    prevPathnameRef.current = pathname
    const prevWasCatalogCategory = prev != null && !prev.includes('/product/')
    if (prevWasCatalogCategory && prev !== pathname) {
      onClickReset()
      setActiveFilters([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    if (Object.keys(formErrors).length > 0) {
      console.error('Form errors:', formErrors)
    }
  }, [formErrors])

  // Initialize expand state for SELECT filters
  useEffect(() => {
    setSelectFiltersExpanded(prev => {
      const newState = Object.keys(selectFilters).reduce((acc, param) => {
        // Use previous value if it exists, otherwise default to false
        acc[param] = prev[param] ?? false
        return acc
      }, {} as Record<string, boolean>)

      // Only update if keys changed
      const prevKeys = Object.keys(prev).sort().join(',')
      const newKeys = Object.keys(newState).sort().join(',')

      return prevKeys === newKeys ? prev : newState
    })
  }, [selectFilters])

  useEffect(() => {
    // Skip if values match current URL (prevents loop)
    const sortMatchesUrl = watchedValues.sort === (searchParamSort || 'recommended')

    const brandMatchesUrl = (() => {
      const urlBrands = searchParamBrand
        ? (Array.isArray(searchParamBrand) ? searchParamBrand : [searchParamBrand])
        : []
      return arraysContainsSameValues(watchedValues.brand || [], urlBrands)
    })()

    // Check boolean filters
    const booleanFiltersMatchUrl = booleanFilters.every(({ param, filter }) => {
      const urlValue = searchParams.get(filter.slug)  // Check URL using filter.slug
      const formValue = watchedValues[param]          // Check form using param
      return (urlValue === 'true') === !!formValue    // Compare 'true' string to boolean
    })

    // Check SELECT filters (like brands)
    const selectFiltersMatchUrl = Object.entries(selectFilters).every(([param, filters]) => {
      const parentSlug = filters[0]?.parentSlug
      const urlValue = searchParams.get(parentSlug)
      const urlValues = urlValue ? urlValue.split(',') : []
      const formValues = watchedValues[param] as string[] || []
      return arraysContainsSameValues(formValues, urlValues)
    })

    // Only submit if values DON'T match URL
    if (!sortMatchesUrl || !brandMatchesUrl || !booleanFiltersMatchUrl || !selectFiltersMatchUrl) {
      handleSubmit(onSubmit)()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedValues.sort,
    watchedValues.brand,
    booleanFilters,
    booleanFilterValuesString,
    selectFilters,
    selectFilterValuesString
  ])

  useEffect(() => {
    if(!isActive) {
      setSortBlockExpanded(false)
      setBrandsBlockExpanded(false)
      setHealthBlockExpanded(false)
      setSelectFiltersExpanded({})
    }

    if(payload?.sort?.expanded) setSortBlockExpanded(true)
    if(payload?.brand?.expanded) setBrandsBlockExpanded(true)
    if(payload?.health?.expanded) setHealthBlockExpanded(true)

    // Handle dynamic SELECT filter expansion from payload
    Object.keys(selectFilters).forEach(param => {
      if (payload?.[param]?.expanded) {
        setSelectFiltersExpanded(prev => ({ ...prev, [param]: true }))
      }
    })
  }, [payload, isActive, selectFilters])

  return (
    <Form onSubmit={handleSubmit(onSubmit)} initial='inactive' animate='active' exit='exit' variants={listVariants}>
      {/* SORT */}
      <fieldset>
        <legend>
          <button type='button' onClick={() => onClickToggleFieldset('sort')} aria-controls='sort-filters'>
            {t('project.modals.filters.sort')}
            <div className={`img-wrapper${sortBlockExpanded ? ' expanded' : ''}`}>
              <Img media={mediaChevron} lazy={true} />
            </div>
          </button>
          <motion.p className='resume' initial={{ opacity: 0 }} animate={sortBlockExpanded ? { opacity: 0 } : { opacity: 1 }} transition={{ delay: .1 }}>
            {sortOptions.find(option => option.value === watchedValues.sort)?.name || ''}
          </motion.p>
        </legend>
        <motion.div id='sort-filters' initial={'inactive'} animate={sortBlockExpanded ? 'active' : 'inactive'} exit={'exit'} variants={blockVariants} aria-expanded={sortBlockExpanded}>
          {SortInputs}
        </motion.div>
      </fieldset>
      {/* DYNAMIC BOOLEANS */}
      {booleanFilters?.length > 0 &&
        <fieldset>
          <legend>
            <button type='button' onClick={() => onClickToggleFieldset('health')} aria-controls='health-filters'>
              {t('project.modals.filters.health_and_origin')}
              <div className={`img-wrapper${healthBlockExpanded ? ' expanded' : ''}`}>
                <Img media={mediaChevron} lazy={true} />
              </div>
            </button>
            <motion.p className='resume' initial={{ opacity: 0 }} animate={healthBlockExpanded ? { opacity: 0 } : { opacity: 1 }} transition={{ delay: .1 }}>
              {/* Get all the names of the watchesValues.[dynamic] from currentFilters */}
              {currentFilters && booleanFilters.map(({ param, filter }) => (
                watchedValues[param] ? filter.name : null
              )).filter(Boolean).join(', ') || ''}
            </motion.p>
          </legend>
          <motion.div id='health-filters' initial={'inactive'} animate={healthBlockExpanded ? 'active' : 'inactive'} exit={'exit'} variants={blockVariants} aria-expanded={healthBlockExpanded}>
            {BooleanInputs}
          </motion.div>
        </fieldset>
      }
      {/* DYNAMIC SELECT */}
      {SelectFilterFieldsets}
      {/* BRANDS */}
      {BrandInputs?.length > 1 &&
        <fieldset>
          <legend>
            <button type='button' onClick={() => onClickToggleFieldset('brands')} aria-controls='brands-filters'>
              {t('project.modals.filters.brands')}
              <div className={`img-wrapper${brandsBlockExpanded ? ' expanded' : ''}`}>
                <Img media={mediaChevron} lazy={true} />
              </div>
            </button>
            <motion.p className='resume' initial={{ opacity: 0 }} animate={brandsBlockExpanded ? { opacity: 0 } : { opacity: 1 }} transition={{ delay: .1 }}>
              {/* Get all the names of the watchesValues.brand from currentFilters */}
              {currentFilters.brands.filter(item => watchedValues.brand.includes(item.slug)).map(item => item.name).join(', ') || ''}
            </motion.p>
          </legend>
          <motion.div id='brands-filters' initial={'inactive'} animate={brandsBlockExpanded ? 'active' : 'inactive'} exit={'exit'} variants={blockVariants} aria-expanded={brandsBlockExpanded}>
            {BrandInputs}
          </motion.div>
        </fieldset>
      }
      <div className='actions'>
        <Button type='button' onClick={closeModal}>{t('project.modals.filters.show')}</Button>
        <Button type='button' onClick={onClickReset} outline={true} color={'--color-text--darker'}>{t('actions.delete_all')}</Button>
      </div>
    </Form>
  )
})

const Form = styled(motion.form)`
  fieldset {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 2px;
    justify-content: flex-start;
    margin-bottom: 40px;

    > div {
      width: 100%;

      &[aria-expanded='false'] {
        overflow: hidden;

        label {
          pointer-events: none;
        }
      }
    }
  }

  legend {
    font-weight: bold;
    position: relative;
    width: 100%;

    button {
      align-items: center;
      display: flex;
      font-family: var(--font-graphik);
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-between;
      width: 100%;

      .img-wrapper {
        align-items: center;
        display: flex;
        height: 29px;
        justify-content: center;
        width: 27px;

        &.expanded {
          img {
            transform: rotate(90deg);
          }
        }
      }

      img {
        transform-origin: center;
        transition: 300ms transform ease;
        width: 5px;
      }
    }
  }

  .resume {
    bottom: -10px;
    ${presetsFluid.font10()}
    font-weight: 300;
    max-width: 100%;
    overflow: hidden;
    pointer-events: none;
    position: absolute;
    text-overflow: ellipsis;
    text-transform: capitalize;
		white-space: nowrap;
  }

  > div {
    align-items: center;
    /* background-color: var(--color-background); */
    bottom: 8px;
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    height: 48px;
    justify-content: space-between;
		max-width: calc(100% - 40px);
    position: fixed;
    width: 100%;
    z-index: 5;

		${mq.greaterThan('nexus7')} {
			max-width: 320px;
		}

    ${mq.greaterThan('tablet')} {
      gap: 15px;
    }

    button {
      margin: 0;
      max-height: 48px;
      width: 100%;

      span {
        ${presets.font15()}
        white-space: nowrap;
      }

      &:last-child {
        background-color: var(--color-background);
      }
    }
  }
`

const Label = styled(motion.label)<{ $checked?: boolean }>`
  align-items: center;
  color: var(--color-text--darker);
  cursor: pointer;
  display: flex;
  height: 48px;
  justify-content: space-between;
  opacity: ${({ $checked }) => $checked ? 1 : .6};
  width: 100%;

  > input {
    display: none;
  }

  > div {
    height: 29px;
    position: relative;
    width: 27px;
  }

  svg {
    transition: opacity 250ms ease-out;

    path {
      transition: fill 250ms ease-out;
    }
  }

  img {
    height: 12px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%) scale(${({ $checked }) => $checked ? 1 : 0});
    transition: transform 200ms ease;
    user-select: none;
    width: 12px;
  }
`

const DivCircle = styled.div<{ $checked: boolean }>`
  background-color: var(--color-filters-bg);
  border-radius: 100px;
  height: 9px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%) scale(${({ $checked }) => $checked ? 1 : 0});
  transition: transform 200ms ease;
  user-select: none;
  width: 9px;
`


const HexagonSvg = memo(({ checked }: { checked: boolean }) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='27' height='29' viewBox='0 0 27 29'><path d='M23.75 6.13l-8-4.54c-1.39-.79-3.11-.79-4.5 0l-8 4.54A4.4 4.4 0 0 0 1 9.96v9.08a4.4 4.4 0 0 0 2.25 3.83l8 4.54c1.39.79 3.11.79 4.5 0l8-4.54A4.4 4.4 0 0 0 26 19.04V9.96a4.4 4.4 0 0 0-2.25-3.83' fill={checked ? 'var(--color-text--darker)' : 'var(--color-filters-bg)'} stroke='var(--color-text--darker)' /></svg>
  )
})

const mediaTick = {
  type: 'image' as const,
  alt: 'Icon tick',
  default: {
    src: '/images/svg/i--tick.svg',
    size: { width: 9, height: 9 }
  }
}

const mediaChevron = {
  type: 'image' as const,
  alt: 'Icon chevron',
  default: {
    src: '/images/svg/i--chevron.svg',
    size: { width: 6, height: 8 }
  }
}


const transition = {
  ...getTransition()
}

const listVariants:Variants = {
  inactive: {
    opacity: 0,
    transition
  },
  active: {
    opacity: 1,
    transition: {
      ...getTransition(),
      staggerChildren: .01
    }
  },
  exit: {
    opacity: 0,
    transition
  }
}

export const itemVariants:(checked:boolean) => Variants = (checked: boolean) => ({
  inactive: {
    x: -2,
    opacity: 0,
    transition
  },
  active: {
    x: 0,
    opacity: checked ? 1 : .6,
    transition
  },
  exit: {
    opacity: 0
  }
})

export const blockVariants:Variants = {
  inactive: {
    height: 0,
    opacity: 0,
    transition: {
      ...getTransition()
    }
  },
  active: {
    height: 'auto',
    opacity: 1,
    transition: {
      ...getTransition(),
      staggerChildren: .01,
    }
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      ...getTransition(),
    }
  }
}